// @ts-nocheck
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Send, RefreshCw, Mail } from 'lucide-react';
import { EmailTestPanel } from './EmailTestPanel';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface PendingEmail {
  id: string;
  type: string;
  recipient_email: string;
  subject?: string;
  content?: string;
  created_at: string;
  metadata: any;
}

export const EmailSendingPanel: React.FC = () => {
  const [pendingEmails, setPendingEmails] = useState<PendingEmail[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchPendingEmails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('communication_logs')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingEmails(data || []);
    } catch (error) {
      console.error('Error fetching pending emails:', error);
      toast.error('Failed to fetch pending emails');
    } finally {
      setLoading(false);
    }
  };

  const sendPendingEmails = async () => {
    setSending(true);
    try {
      const { data, error } = await invokeEdgeFunction('send-communication-email');

      if (error) throw error;

      toast.success(`Email sending triggered: ${data.message}`);
      
      // Refresh the pending emails list
      await fetchPendingEmails();
    } catch (error) {
      console.error('Error triggering email sending:', error);
      toast.error('Failed to trigger email sending');
    } finally {
      setSending(false);
    }
  };

  React.useEffect(() => {
    fetchPendingEmails();
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Queue Management
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPendingEmails}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={sendPendingEmails}
              disabled={sending || pendingEmails.length === 0}
              size="sm"
            >
              <Send className={`h-4 w-4 mr-2 ${sending ? 'animate-pulse' : ''}`} />
              {sending ? 'Sending...' : 'Send All Pending'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <EmailTestPanel />
        
        <div className="space-y-4 mt-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {pendingEmails.length} pending emails
            </div>
            {pendingEmails.length > 0 && (
              <Badge variant="secondary">
                Ready to Send
              </Badge>
            )}
          </div>

          {pendingEmails.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No pending emails</p>
              <p className="text-sm">All emails have been sent!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingEmails.slice(0, 5).map((email) => (
                <div key={email.id} className="border rounded-lg p-3 bg-muted/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-sm">{email.recipient_email}</div>
                    <Badge variant="outline">{email.type}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Created: {new Date(email.created_at).toLocaleString()}
                  </div>
                  {email.metadata?.athlete_name && (
                    <div className="text-xs text-muted-foreground">
                      Athlete: {email.metadata.athlete_name}
                    </div>
                  )}
                  {email.metadata?.team_name && (
                    <div className="text-xs text-muted-foreground">
                      Team: {email.metadata.team_name}
                    </div>
                  )}
                </div>
              ))}
              {pendingEmails.length > 5 && (
                <div className="text-center text-sm text-muted-foreground">
                  ... and {pendingEmails.length - 5} more
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};